import { h, tableScroll } from '../components/dom.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import { router } from '../router/router.js';
import { showInitialSetup, pendingApprovalsLabel } from '../lib/onboarding.js';
import { dateTimeEl } from '../lib/timezone.js';

export async function renderDashboard(): Promise<void> {
  const main = renderShell('/');
  const createBtn = h('a', { class: 'btn btn-primary btn-sm', href: '#/links' }, 'Create link');
  main.append(pageHead('Dashboard', 'Welcome back.', createBtn));

  const kpis = h('div', { class: 'grid grid-4' });
  main.append(kpis);

  const [me, profiles, links, subs, contacts, fields, approvalsCount, emailState] = await Promise.all([
    api.me().catch(() => null),
    api.profiles().catch(() => ({ data: [] })),
    api.shareLinks().catch(() => ({ data: [] })),
    api.subscribers().catch(() => ({ data: [] })),
    api.contacts().catch(() => ({ data: [] })),
    api.fields().catch(() => ({ data: [] })),
    api.approvalsCount().catch(() => 0),
    api.auth.email.get().catch(() => null),
  ]);

  const activeLinks = links.data.filter((l) => l.status === 'active').length;

  // Initial Setup onboarding checklist — shown at the very TOP (before KPIs) for
  // the first 14 days since account creation, then hidden on subsequent logins.
  // Linkable steps guide the new user through the core first-run flow.
  if (me && showInitialSetup(me.created_at)) {
    main.insertBefore(initialSetupSection(), kpis);
  }

  // Pending-approvals banner — highest-priority prompt, shown ABOVE everything
  // (including Initial Setup) whenever there are subscription requests awaiting
  // the owner's approval. Links straight to /approvals.
  const pendingApprovals = approvalsCount > 0
    ? approvalsCount
    : subs.data.filter((s) => s.status === 'pending').length;
  if (pendingApprovals > 0) {
    main.insertBefore(pendingApprovalsBanner(pendingApprovals), main.firstChild!.nextSibling);
  }

  // Add-email prompt — shown near the top when the user has NO verified primary
  // email, because email is what powers activity notifications (someone adds you
  // / a contact updates their info). Links straight to the Settings › Primary
  // email section so they can add one. Only suppressed once an email is verified;
  // a pending-but-unverified email still shows the prompt (they must finish).
  // Placed just BELOW the pending-approvals banner (approvals stay highest).
  if (emailState && !emailState.verified) {
    const anchor = main.firstChild!.nextSibling;
    if (pendingApprovals > 0 && anchor) {
      main.insertBefore(addEmailBanner(), anchor.nextSibling);
    } else {
      main.insertBefore(addEmailBanner(), anchor);
    }
  }

  const subCount = me?.subscriber_count ?? subs.data.filter((s) => s.status === 'active').length;

  const kpi = (label: string, value: number | string, href: string) =>
    h('a', { class: 'card kpi-card', href: '#' + href, 'aria-label': `${label}: ${value} — view` },
      h('div', { class: 'muted' }, label), h('div', { class: 'kpi' }, String(value)),
      h('div', { class: 'kpi-cta muted' }, h('small', {}, 'View →')));
  kpis.append(
    kpi('Profiles', profiles.data.length, '/profiles'),
    kpi('Active links', activeLinks, '/links'),
    kpi('Subscribers', subCount, '/subscribers'),
    kpi('Contacts', contacts.data.length, '/contacts'),
  );

  // Welcome line: prefer real name from fields (given/family), fall back to
  // display_name's first token. Show the handle underneath as a link to the
  // public profile (virtualid.one/id/<handle>).
  const fieldVal = (t: string) =>
    (fields.data.find((f) => (f.type || f.field_type_key) === t)?.value || '').trim();
  const given = fieldVal('given_name');
  const family = fieldVal('family_name');
  const fullName = [given, family].filter(Boolean).join(' ');
  const greetName = fullName || me?.username || (me?.display_name ? me.display_name.split(' ')[0] : '');
  const handle = me?.handle || me?.username || '';
  const headP = main.querySelector('.page-head p') as HTMLElement | null;
  if (headP) {
    headP.replaceChildren();
    if (greetName) headP.append(document.createTextNode(`Welcome back, ${greetName}.`));
    if (me?.created_at) {
      const d = new Date(me.created_at);
      if (!isNaN(d.getTime())) {
        headP.append(h('br'));
        headP.append(h('span', { class: 'muted', style: 'font-size:.92em' },
          'User since: ', dateTimeEl(me.created_at, { dateOnly: true })));
      }
    }
    if (handle) {
      headP.append(h('br'));
      headP.append(h('a', {
        class: 'muted handle-link',
        href: `#/id/${handle}`,
        style: 'font-size:.92em',
      }, `virtualid.one/id/${handle}`));
    }
  }

  // Recent activity — derived from subscriptions + links (server has no /activity in v1 yet).
  main.append(h('h3', { style: 'margin-top:30px' }, 'Recent activity'));
  const tbody = h('tbody');
  const pending = subs.data.filter((s) => s.status === 'pending');
  for (const s of pending.slice(0, 3)) {
    tbody.append(h('tr', {},
      h('td', {}, 'Pending'),
      h('td', {}, h('span', { class: 'badge badge-warn' }, 'Approval')),
      h('td', { html: `<strong>${esc(s.subscriber?.username || s.subscriber?.display_name || 'Someone')}</strong> requested access` })));
  }
  for (const s of subs.data.filter((s) => s.status === 'active').slice(0, 3)) {
    tbody.append(h('tr', {},
      h('td', {}, s.since ? dateTimeEl(s.since) : 'recently'),
      h('td', {}, h('span', { class: 'badge badge-ok' }, 'Subscribed')),
      h('td', { html: `<strong>${esc(s.subscriber?.username || s.subscriber?.display_name || 'Someone')}</strong> subscribed` })));
  }
  if (!tbody.children.length) tbody.append(h('tr', {}, h('td', { colspan: '3', class: 'muted' }, 'No recent activity.')));
  main.append(h('div', { class: 'card', style: 'padding:0' },
    tableScroll(h('table', { class: 'table' },
      h('thead', {}, h('tr', {}, h('th', {}, 'When'), h('th', {}, 'Event'), h('th', {}, 'Detail'))),
      tbody))));

  main.append(h('h3', { style: 'margin-top:30px' }, 'Quick actions'));
  main.append(h('div', { class: 'row' },
    quick('Create share link', '/links', true),
    quick('New profile', '/profiles'),
    quick('Add a field', '/fields'),
    quick('Review subscribers', '/subscribers'),
  ));
  main.append(portalFooter());
}

// pendingApprovalsBanner builds the top-of-dashboard prompt shown whenever the
// owner has subscription requests awaiting approval. It states the count and
// links to /approvals. Shown above Initial Setup + KPIs.
// addEmailBanner prompts the user to add a primary email so they can receive
// activity notifications (someone adds you / a contact updates their info).
// "Add email →" deep-links to Settings › Primary email (?section=email), which
// scrolls that section into view.
function addEmailBanner(): HTMLElement {
  const add = h('a', { class: 'btn btn-primary btn-sm', href: '#/settings?section=email' }, 'Add email →');
  add.addEventListener('click', (e) => { e.preventDefault(); router.navigate('/settings?section=email'); });
  return h('div', { class: 'approvals-alert', role: 'status' },
    h('span', { class: 'approvals-alert-dot', 'aria-hidden': 'true' }),
    h('div', { class: 'approvals-alert-text' }, 'Add your email address',
      h('div', { class: 'muted', style: 'font-weight:400;font-size:.85rem' },
        'Add an email so you can be notified when someone adds you or updates their information.')),
    add);
}

function pendingApprovalsBanner(count: number): HTMLElement {
  const label = pendingApprovalsLabel(count);
  const review = h('a', { class: 'btn btn-primary btn-sm', href: '#/approvals' }, 'Review →');
  review.addEventListener('click', (e) => { e.preventDefault(); router.navigate('/approvals'); });
  return h('div', { class: 'approvals-alert', role: 'status' },
    h('span', { class: 'approvals-alert-dot', 'aria-hidden': 'true' }),
    h('div', { class: 'approvals-alert-text' }, label,
      h('div', { class: 'muted', style: 'font-weight:400;font-size:.85rem' },
        'People are requesting access to your profiles.')),
    review);
}

function quick(label: string, path: string, primary = false): HTMLElement {
  const a = h('a', { class: 'btn ' + (primary ? 'btn-primary' : 'btn-ghost'), href: '#' + path }, label);
  a.addEventListener('click', (e) => { e.preventDefault(); router.navigate(path); });
  return a;
}

// initialSetupSection builds the first-run "Initial Setup" checklist shown at the
// top of the dashboard for new accounts. Each step is a linkable card that routes
// to the relevant view (add fields -> create profile -> create share link).
function initialSetupSection(): HTMLElement {
  const step = (n: number, title: string, desc: string, path: string): HTMLElement => {
    const card = h('a', {
      class: 'card setup-step',
      href: '#' + path,
      'aria-label': `Step ${n}: ${title}`,
    },
      h('div', { class: 'setup-step-num' }, String(n)),
      h('div', { class: 'setup-step-body' },
        h('div', { class: 'setup-step-title' }, title),
        h('div', { class: 'muted', style: 'font-size:.86rem' }, desc)),
      h('div', { class: 'setup-step-cta muted' }, h('small', {}, 'Go →')));
    card.addEventListener('click', (e) => { e.preventDefault(); router.navigate(path); });
    return card;
  };
  // Non-clickable informational step (no link/navigation).
  const infoStep = (n: number, title: string, desc: string): HTMLElement =>
    h('div', { class: 'card setup-step setup-step-static' },
      h('div', { class: 'setup-step-num' }, String(n)),
      h('div', { class: 'setup-step-body' },
        h('div', { class: 'setup-step-title' }, title),
        h('div', { class: 'muted', style: 'font-size:.86rem' }, desc)));
  return h('section', { class: 'setup-section' },
    h('h3', { style: 'margin-bottom:4px' }, 'Initial Setup'),
    h('p', { class: 'muted', style: 'margin-bottom:14px;font-size:.9rem' },
      'Get your VirtualID ready in a few quick steps.'),
    h('div', { class: 'grid grid-3 setup-steps' },
      step(1, 'Add fields', 'Add the details you may want to share — email, phone, socials, and more.', '/fields'),
      step(2, 'Create a profile', 'Create a new profile and choose which fields it exposes.', '/profiles'),
      step(3, 'Create a shareable link', 'Generate an invite link or QR code to share a profile.', '/links'),
      step(4, 'Approve subscribers (optional)', 'Review and approve people requesting access to your profiles.', '/approvals'),
      step(5, 'Access My Contacts', 'See the people you’ve connected with and their shared details.', '/contacts'),
      infoStep(6, 'Create additional profiles and shareable links', 'Tailor a profile and link for each context (e.g. Friends, Work, Business).'),
    ));
}

export function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}
